import {
  Flex,
  Image,
  ImageProps,
  Input,
  InputGroup,
  InputProps
} from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import { ReactNode } from "react"
import { useSize } from "../../utils/size"
import Pathicon from "../pathicon/Pathicon"

export type ItemInputValue = {
  name: string
}

export const ItemInputImage = ({
  image,
  ...imageProps
}: { image: string } & ImageProps) => {
  return <Image src={image} maxH="24px" borderRadius="md" {...imageProps} />
}

export type ItemInputProps<T extends ItemInputValue> = Omit<
  InputProps,
  "value" | "onChange"
> & {
  value: T
  onChange: (item: T) => void
  icon?: IconName
  leftElement?: ReactNode
}

function ItemInput<T extends ItemInputValue>({
  value,
  onChange,
  icon,
  leftElement,
  ...inputProps
}: ItemInputProps<T>) {
  const [leftElementRef, leftElementSize] = useSize("width")
  const minWidth = "40px"

  const resolvedLeftElement = leftElement ? (
    leftElement
  ) : icon ? (
    <Pathicon
      icon={icon}
      color={inputProps.disabled ? "whiteAlpha.600 !important" : undefined}
    />
  ) : null

  return (
    <InputGroup
      className="ItemInput"
      startElement={
        resolvedLeftElement ? (
          <Flex
            ref={leftElementRef}
            alignItems="center"
            justifyContent="center"
            minW={minWidth}
            h="100%"
          >
            {resolvedLeftElement}
          </Flex>
        ) : undefined
      }
    >
      <Input
        type="text"
        value={value?.name || ""}
        onChange={(e) => onChange({ ...value, name: e.target.value })}
        autoFocus
        borderRadius="xl"
        padding="0 .4em"
        _placeholder={{ fontSize: { base: "sm", md: undefined } }}
        css={{
          "&": {
            paddingInlineStart: leftElementSize
              ? `calc(${leftElementSize}px + 1.4em)`
              : `calc(${minWidth} + 1em)`,
            paddingLeft: leftElementSize
              ? `calc(${leftElementSize}px + 1.4em)`
              : `calc(${minWidth} + 1em)`
          }
        }}
        {...inputProps}
      />
    </InputGroup>
  )
}

export default ItemInput
